import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, ButtonBase, Skeleton, CircularProgress, Alert, IconButton, Tooltip, Collapse, Paper, Stack, ToggleButton, ToggleButtonGroup, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, TextField, Checkbox, Popover, Divider, MenuItem, FormControlLabel,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapseIcon,
  HelpOutline as HelpIcon,
  EventAvailable as InplannenIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useKlantAfkortingen } from '../../utils/klantAfkortingen';
import PageHeader from '../../ui-components/PageHeader';
import AfgerondSignaal from '../../ui-components/AfgerondSignaal';
import { computeDayLoad, weekAveragePct, effectiveTaskHours, taskProjectDelen, DEFAULT_CAPACITY_RULES } from '../../utils/capacityCalc';
import { uitklapRegels, dagTotalen, taskHoursPerDay } from './uitklapRegels';
import CapaciteitsRegels from '../../ui-components/CapaciteitsRegels';
import { useCapacityRules } from '../../utils/useCapacityRules';
import WeekSelector from '../../ui-components/WeekSelector';
import { getMonday, formatDateISO, addDays } from '../../utils/weekUtils';
import { leesCache, lopendeLading, useTraagVerversen, werkCacheBij } from '../../utils/planningCache';
import { haalPersonen, haalTakenRange, haalAgenda, leesAgendaCache, nietGelezen, sleutels, taakGewijzigd } from '../../utils/planningBronnen';
import { registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { schrijfVerzetting } from '../PlannerTodo/verzetten';
import TaakPaneel from '../../ui-components/TaakPaneel/TaakPaneel';
import CapaciteitBlokken from './CapaciteitBlokken';
import { haalDraaidagen, draaidagenUitCache, draaidagTakenPerPersoon, agendaZonderDraaidag, alleDraaidagTaken } from '../../utils/draaidagenPlanning';
import { DAGSTAAT_STIJL } from '../../utils/capacityColors';
import { AFGEROND_SET } from '../../utils/taakStatus';
import {
  persoonLabels, sorteerPersonen, inplanLink, pasAfwezigheidToe, overboekteDagen, celNiveau,
  persoonBezetting, teamTotaal, isOverSignaal, balkDelen, overboektenBovenaan, stabieleVolgorde,
  fmtUrenHeel, fmtUrenHalf, heeftDagOverboeking, leesOpslag, schrijfOpslag, bouwWeekData, agendaEmails, LEGE_AGENDA,
  magInKeuzelijst,
} from './capaciteitPersonen';
// Uren in tooltips en de dag-popover: heel of met ½, en "0" in plaats van niets.
const urenH = (h) => fmtUrenHalf(h) || '0';
// ISO-weeknummer (maandag = eerste dag) voor de weekgrenzen bij 2/4 weken.
const isoWeekNr = (d) => {
  const x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dag = x.getUTCDay() || 7; x.setUTCDate(x.getUTCDate() + 4 - dag);
  const jaarStart = new Date(Date.UTC(x.getUTCFullYear(), 0, 1));
  return Math.ceil((((x - jaarStart) / 86400000) + 1) / 7);
};

// Constants
// Uren per werkdag en de per-type inschatting staan in de capaciteitsregels
// (instelbaar door de planner) — zie utils/capacityCalc.js.
const DAY_NAMES = ['Ma', 'Di', 'Wo', 'Do', 'Vr'];
const formatDateDisplay = (date) => date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });

// ── Rust (Joris, sep 2026: "onze schermen zijn te druk") ──
// Eén accentkleur (FF-primary) voor ruimte, verder neutrale tinten. Rood alleen voor
// echt overboekt, en dan één stip per persoon. Wie gewoon ruimte heeft staat stil.
// Twee tekstgroottes in de tabel: 14px (namen, getallen) en 12px (de rest).
const ACCENT = '#526075';
const ROOD = '#b91c1c';
// Vrije uren: overal één toon, zonder drempel die niemand kan verklaren.
const VRIJ_TOON = '#64748b';
const FS = { groot: '0.875rem', klein: '0.75rem' };
// Kleur per bezetting (Joris, 3 nov: "men wil toch meer kleur hierin, iets harder"):
// ruimte groen, vol (80–100%) amber, over (>100%) rood; een lege dag blijft neutraal.
// Zacht vlak + donkere tekst in dezelfde tint, zodat het leesbaar blijft (AA).
const CEL_TEKST = {
  leeg: '#94a3b8', ruimte: '#047857', vol: '#92400e', over: ROOD,
  vrij: '#475569', draaidag: '#9a3412', draaidag_optie: '#9a3412',
};
const CEL_NIVEAU_VLAK = { ruimte: '#e7f6ee', vol: '#fdf0c8', over: '#fde2e2' };
// Niet beschikbaar: een vlak met een woord erin (waarom). Afwezig = grijs, draaidag =
// oranje (zoals de draaidagen in de Dagstart); optie = zelfde oranje, gestreept.
const CEL_VLAK = {
  vrij: '#e9edf2', draaidag: '#fdebd8',
  draaidag_optie: 'repeating-linear-gradient(135deg, #fef4ea, #fef4ea 6px, #fbdfc4 6px, #fbdfc4 12px)',
};
const celVlak = (staat, niveau) => CEL_VLAK[staat] || CEL_NIVEAU_VLAK[niveau];
// "Vrij" betekende hier twee dingen (afwezig in een cel, vrije uren in de kolom).
// Een afwezige dag heet daarom "Afwezig"; "vrij" is alleen nog open tijd.
const staatTekst = (staat) => (staat === 'vrij' ? 'Afwezig' : DAGSTAAT_STIJL[staat]?.tekst || '');
const fmtDagKort = (iso) => new Date(iso).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });

// Rustige uitklap per persoon (Joris, 25 sep): één regel per project, uren op de dag
// waarop ze vallen, lege dagen leeg. Geen groepskoppen, bolletjes of ruitjes; de
// details (taken, afspraken per dag) staan in de tooltips.
// intern = alleen Full Frame schuift aan (verschuifbaar), extern = klant/derde erbij.
const AFSPRAAK_SOORT = {
  extern: { label: 'extern' },
  intern: { label: 'intern' },
  taak: { label: 'geblokte werktijd' },
};

function getTasksForDay(tasks, dayISO) {
  return (tasks || []).filter(t => {
    const prio = t.prio || t.status || '';
    if (AFGEROND_SET.has(prio)) return false;
    // Een gebundelde rij (meetings) hoort alleen bij de dagen waarop er echt iets staat.
    if (t.hoursByDay) return Boolean(t.hoursByDay[dayISO]);
    const start = t.startDate?.split('T')[0];
    const end = t.endDate?.split('T')[0] || start;
    return start && dayISO >= start && dayISO <= end;
  });
}

const fmtRange = (t) => {
  const s = (t.startDate || '').split('T')[0];
  const e = (t.endDate || t.startDate || '').split('T')[0];
  const d = (iso) => { const x = new Date(iso); return isNaN(x) ? '' : x.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }); };
  return e && e !== s ? `${d(s)}–${d(e)}` : d(s);
};

// Tekst in de uitklap: 13px voor de regels, 12px voor labels. Samen met de 14px van
// de hoofdrij blijft het bij drie groottes.
const FS_REGEL = '0.8125rem';
const TEKST = '#334155';
const STIL = '#94a3b8';

const uurTekst = (h, geschat) => {
  const t = fmtUrenHalf(h);
  return t ? `${geschat ? '~' : ''}${t}` : '';
};

function RegelTooltip({ regel, rules }) {
  return (
    <Box sx={{ p: 0.5, maxWidth: 300 }}>
      <Typography variant="caption" fontWeight={700} display="block">{regel.naam}</Typography>
      {regel.klant && <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem', opacity: 0.75, mb: 0.25 }}>{regel.klant}</Typography>}
      {regel.taken.map((t, i) => {
        const eh = effectiveTaskHours(t, rules);
        return (
          <Typography key={t.id || i} variant="caption" display="block" sx={{ fontSize: '0.7rem' }}>
            {t.name}{t.prio || t.type ? ` · ${t.prio || t.type}` : ''} · {fmtRange(t)}
            {regel.volledag ? '' : ` · ${eh.estimated ? '~' : ''}${fmtUrenHalf(eh.val) || '0'}u${eh.estimated ? ' (ingeschat)' : ''}`}
            {t.deadline ? ` · deadline ${String(t.deadline).split('T')[0]}` : ''}
          </Typography>
        );
      })}
    </Box>
  );
}

// Eén dag agenda in een tooltip (per dagvak van de agendaregel).
function AgendaDagTooltip({ iso, evts }) {
  return (
    <Box sx={{ p: 0.5, maxWidth: 320 }}>
      <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
        {new Date(iso).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'short' })}
      </Typography>
      {evts.map((ev, i) => (
        <Typography key={i} variant="caption" display="block" sx={{ fontSize: '0.7rem' }}>
          {ev.start_time}–{ev.end_time} {ev.subject} ({(AFSPRAAK_SOORT[ev.soort] || AFSPRAAK_SOORT.intern).label})
        </Typography>
      ))}
    </Box>
  );
}

// ─── Uitklap: de taken van één persoon, uitgelijnd onder de dagkolommen ───
function ExpandedTasks({ tasks, days, variant = 'grid', rules = DEFAULT_CAPACITY_RULES, gridCols: gridColsProp }) {
  const dayISOs = useMemo(() => days.map(d => d.iso), [days]);
  const regels = useMemo(() => uitklapRegels(tasks, dayISOs, rules), [tasks, dayISOs, rules]);

  if (!regels.length) {
    return <Typography sx={{ display: 'block', px: 2, py: 1, fontSize: FS.klein, color: STIL }}>Niets gepland in deze periode.</Typography>;
  }

  // ── Mobiel: één regel per project met het totaal ──
  if (variant === 'list') {
    return (
      <Stack spacing={0.25} sx={{ py: 1, px: 1.5 }}>
        {regels.map((r) => (
          <Tooltip key={r.sleutel} arrow placement="top" enterTouchDelay={0} title={<RegelTooltip regel={r} rules={rules} />}>
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ py: 0.25 }}>
              <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: FS_REGEL, color: TEKST }}>
                {r.naam}{r.sub && <span style={{ color: STIL }}> · {r.sub}</span>}
              </Typography>
              <Typography sx={{ fontSize: FS_REGEL, color: TEKST, fontVariantNumeric: 'tabular-nums' }}>
                {r.volledag ? `${Object.keys(r.perDag).length} d` : `${uurTekst(r.totaal, r.geschat) || '0'}u`}
              </Typography>
            </Stack>
          </Tooltip>
        ))}
      </Stack>
    );
  }

  // ── Desktop: zelfde raster als de hoofdtabel, zodat de dagen exact onder elkaar liggen ──
  const gridCols = gridColsProp || `220px repeat(${days.length}, minmax(80px, 1fr)) 70px`;
  const totalen = regels.length > 1 ? dagTotalen(regels, dayISOs) : null;
  const weekLijn = (d) => (days.length > 5 && d.weekStart ? '1px solid #eef0f3' : undefined);
  return (
    <Box sx={{ pb: 1 }}>
      {regels.map((r) => (
        <Box key={r.sleutel} sx={{ display: 'grid', gridTemplateColumns: gridCols, alignItems: 'center', '&:hover': { bgcolor: '#f4f6f9' } }}>
          <Tooltip arrow placement="top-start" enterTouchDelay={0}
            title={r.agenda ? `Agenda${r.sub ? ` · ${r.sub}` : ''} — wijs een dag aan voor de afspraken van die dag` : <RegelTooltip regel={r} rules={rules} />}>
            <Typography noWrap sx={{ px: 2, py: 0.5, fontSize: FS_REGEL, color: TEKST, minWidth: 0 }}>
              {r.naam}{r.sub && <span style={{ color: STIL }}> · {r.sub}</span>}
            </Typography>
          </Tooltip>
          {days.map((d) => {
            const h = r.perDag[d.iso];
            const tekst = h && r.volledag ? 'dag' : uurTekst(h, r.geschat);
            const cel = (
              <Typography key={d.iso} sx={{
                textAlign: 'center', py: 0.5, fontSize: FS_REGEL, color: r.volledag ? STIL : TEKST,
                fontVariantNumeric: 'tabular-nums', alignSelf: 'stretch', borderLeft: weekLijn(d),
              }}>
                {tekst}
              </Typography>
            );
            const evts = r.agendaEvents?.[d.iso];
            return evts?.length ? (
              <Tooltip key={d.iso} arrow placement="top" enterTouchDelay={0} title={<AgendaDagTooltip iso={d.iso} evts={evts} />}>
                {cel}
              </Tooltip>
            ) : cel;
          })}
          <Typography sx={{ textAlign: 'right', pr: 2, fontSize: FS.klein, color: STIL, fontVariantNumeric: 'tabular-nums' }}>
            {r.volledag ? `${Object.keys(r.perDag).length} d` : `${uurTekst(r.totaal, r.geschat) || '0'}u`}
          </Typography>
        </Box>
      ))}
      {/* Totaal alleen als er iets op te tellen valt (meer dan één regel). */}
      {totalen && (
        <Box sx={{ display: 'grid', gridTemplateColumns: gridCols, alignItems: 'center', borderTop: '1px solid #eef0f3', mt: 0.25 }}>
          <Typography sx={{ px: 2, py: 0.5, fontSize: FS.klein, color: STIL }}>Totaal</Typography>
          {days.map((d) => {
            const t = totalen[d.iso];
            const over = t > rules.hoursPerDay + 0.05;
            return (
              <Typography key={d.iso} sx={{ textAlign: 'center', py: 0.5, fontSize: FS_REGEL, fontWeight: over ? 700 : 500,
                color: over ? ROOD : TEKST, fontVariantNumeric: 'tabular-nums', borderLeft: weekLijn(d) }}>
                {fmtUrenHalf(t)}
              </Typography>
            );
          })}
          <Typography sx={{ textAlign: 'right', pr: 2, fontSize: FS.klein, color: STIL, fontVariantNumeric: 'tabular-nums' }}>
            {fmtUrenHalf(Object.values(totalen).reduce((s, h) => s + h, 0)) || '0'}u
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// Inhoud van één dag van één persoon: dagstaat, externe afspraken en taken met de
// uren van DIE dag. Gedeeld door de hover-tooltip (donker) en de klik-popover (licht).
function DagInhoud({ titel, stijl, extra = 0, externeAfspraken = [], dayTasks = [], iso, dayISOs, rules, perTaak, licht = false }) {
  return (
    <Box sx={{ p: 0.5 }}>
      {titel && (
        <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>{titel}</Typography>
      )}
      {stijl && (
        <Typography variant="caption" display="block" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
          {stijl.titel}
          {extra > 0 && ` — let op: er staat ook nog ${urenH(extra)}u ander werk op deze dag`}
        </Typography>
      )}
      {externeAfspraken.map((ev, i) => (
        <Typography key={`ext-${i}`} variant="caption" display="block" sx={{ fontSize: '0.7rem', color: licht ? '#0284c7' : '#7dd3fc', fontWeight: 700 }}>
          Externe afspraak: {ev.start_time}–{ev.end_time} {ev.subject}
        </Typography>
      ))}
      {dayTasks.map((t, i) => {
        // De uren van DEZE dag, niet het totaal van de taak. Een meerdaagse taak of
        // de agenda-regel (die de hele periode beslaat) liet anders het totaal zien:
        // Michel kreeg zo "128u" te zien op één woensdag (Joris, 9 sep).
        // De verdeling komt uit computeDayLoad (perTaak), dezelfde als de rij en de
        // uitklap: meerdaags werk vult eerst de ruimte rond vast werk en meetings.
        // Alleen wat die berekening niet verdeelt (agenda-regel) valt terug op de dagverdeling.
        const berekend = perTaak?.get(t);
        const opDezeDag = berekend ? (berekend[iso] || 0) : taskHoursPerDay(t, dayISOs, rules)[iso];
        const urenTekst = berekend || opDezeDag
          ? `(${urenH(opDezeDag)}u)`
          : (t.hours ? `(${urenH(t.hours)}u totaal)` : '');
        return (
          <Typography key={i} variant="caption" display="block" sx={{ fontSize: '0.7rem', mb: 0.25 }}>
            {taskProjectDelen(t).label
              ? <><b>{taskProjectDelen(t).label}</b>{` · ${t.name}`}</>
              : t.name} {urenTekst}
          </Typography>
        );
      })}
    </Box>
  );
}

// Breedte van de naamkolom (per dag / in de uitklap): 180–700 px, standaard 220.
const KOLOM_MIN = 180;
const KOLOM_MAX = 700;
const KOLOM_STANDAARD = 220;
export const begrensKolom = (px) => Math.min(KOLOM_MAX, Math.max(KOLOM_MIN, Math.round(Number(px) || KOLOM_STANDAARD)));

/**
 * Sleeprand op de rechterkant van de naamkolom: slepen = breder/smaller, dubbelklik =
 * standaardbreedte, pijltjestoetsen = 20 px (toetsenbord). onZet(px, opslaan) krijgt
 * tijdens het slepen opslaan=false en bij loslaten true.
 */
function KolomRand({ breedte, onZet }) {
  const start = (e) => {
    if (e.button !== 0) return;
    e.preventDefault(); e.stopPropagation();
    const x0 = e.clientX;
    const b0 = breedte;
    let laatste = b0;
    const ac = new AbortController();
    const beweeg = (ev) => { laatste = begrensKolom(b0 + ev.clientX - x0); onZet(laatste, false); };
    const los = () => {
      ac.abort();
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      onZet(laatste, true);
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', beweeg, { signal: ac.signal });
    document.addEventListener('mouseup', los, { signal: ac.signal });
    window.addEventListener('blur', los, { signal: ac.signal });
  };
  const toets = (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault(); e.stopPropagation();
    onZet(breedte + (e.key === 'ArrowRight' ? 20 : -20), true);
  };
  return (
    <Box role="separator" tabIndex={0} aria-orientation="vertical"
      aria-label="Kolombreedte: sleep om aan te passen, dubbelklik voor standaard"
      aria-valuenow={breedte} aria-valuemin={KOLOM_MIN} aria-valuemax={KOLOM_MAX}
      title="Sleep om de kolom breder of smaller te maken · dubbelklik = standaard"
      onMouseDown={start} onKeyDown={toets} onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => { e.stopPropagation(); onZet(KOLOM_STANDAARD, true); }}
      sx={{
        position: 'absolute', top: 0, bottom: 0, right: -5, width: 10, cursor: 'col-resize', zIndex: 2, outline: 'none',
        '&::after': { content: '""', position: 'absolute', top: 6, bottom: 6, left: 4, width: '1px', bgcolor: '#e2e8f0' },
        '&:hover::after, &:focus-visible::after': { bgcolor: ACCENT, width: '2px' },
      }} />
  );
}

// Overzicht: naam · bezettingsbalk · % · vrij.
const OVERZICHT_GRID = 'minmax(220px, 300px) minmax(160px, 1fr) 72px 104px';

// Eén horizontale bezettingsbalk over de periode: accent tot 100%, een streep op 100%,
// en alleen wat daarboven zit is rood (de balk loopt door tot 125%).
function Bezettingsbalk({ pct, label }) {
  const { vol, over, streep } = balkDelen(pct);
  return (
    <Box role="img" aria-label={label} sx={{ position: 'relative', width: '100%', height: 6, borderRadius: 3, bgcolor: '#eef1f5' }}>
      <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${vol}%`, bgcolor: ACCENT, borderRadius: over > 0 ? '3px 0 0 3px' : 3 }} />
      {over > 0 && (
        <Box sx={{ position: 'absolute', left: `${streep}%`, top: 0, bottom: 0, width: `${over}%`, bgcolor: ROOD, borderRadius: '0 3px 3px 0' }} />
      )}
      <Box sx={{ position: 'absolute', left: `${streep}%`, top: -3, bottom: -3, width: '1px', bgcolor: '#94a3b8' }} />
    </Box>
  );
}

// Robust string conversion - handles arrays, JSON strings of arrays, null, objects
const safeStr = (v) => {
  if (v == null) return '';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'string') {
    if (v.startsWith('[')) {
      try {
        const arr = JSON.parse(v);
        if (Array.isArray(arr)) return arr.join(', ');
      } catch { /* fall through */ }
    }
    return v;
  }
  return String(v);
};
const safeLower = (v) => safeStr(v).toLowerCase();

// ─── Main Component ───
// Ophaalperiode: maandag t/m vrijdag van de laatste week.
const periode = (start, aantalWeken) => ({
  startDate: formatDateISO(start),
  endDate: formatDateISO(addDays(start, aantalWeken * 7 - 3)),
});
const NAAMKOLOM_KEY = 'ff.capaciteit.naamkolom';
// Agenda over ma t/m zó van de laatste week: zelfde verzoek als het Rooster, dus één
// gedeelde cachesleutel. Wat ná de laatste vrijdag valt knippen we er hier weer af.
const agendaPeriode = (start, aantalWeken) => ({
  startDate: formatDateISO(start),
  endDate: formatDateISO(addDays(start, aantalWeken * 7 - 1)),
});

/** Engine-agenda beperken tot t/m `tot` (YYYY-MM-DD); niet-gelezen mailboxen blijven mee. */
export function knipAgenda(ruw, tot) {
  const perDatum = (obj) => {
    const uit = {};
    Object.entries(obj || {}).forEach(([e, dagen]) => {
      uit[e] = Object.fromEntries(Object.entries(dagen || {}).filter(([iso]) => iso <= tot));
    });
    return uit;
  };
  const events = {};
  Object.entries(ruw?.events || {}).forEach(([e, lijst]) => {
    events[e] = (lijst || []).filter(ev => String(ev?.date || '') <= tot);
  });
  return { hours: perDatum(ruw?.hours), events, afwezig: perDatum(ruw?.afwezig), niet_gelezen: nietGelezen(ruw) };
}

/**
 * Nieuwe datums voor taken (op id) in een lijst per persoon — ook als dezelfde taak bij
 * meerdere uitvoerders staat. `wijzigingen`: [{ id, startDate, endDate }].
 */
export function zetDatums(perPersoon, wijzigingen) {
  const nieuw = new Map((wijzigingen || []).map(w => [w.id, w]));
  const uit = {};
  Object.entries(perPersoon || {}).forEach(([pid, lijst]) => {
    uit[pid] = (lijst || []).map(t => (nieuw.has(t?.id) ? { ...t, startDate: nieuw.get(t.id).startDate, endDate: nieuw.get(t.id).endDate } : t));
  });
  return uit;
}

const takenArgs = (persons, start, aantalWeken) => ({ personIds: (persons || []).map(p => p.id), ...periode(start, aantalWeken) });
const agendaArgs = (persons, start, aantalWeken) => ({ emails: agendaEmails(persons), ...agendaPeriode(start, aantalWeken) });

/** Wat er voor deze periode al in de gedeelde cache ligt: { persons, basis?, agenda? } of null. */
function capaciteitUitCache(start, aantalWeken) {
  const p = leesCache(sleutels.personen());
  if (!p) return null;
  const persons = p.waarde || [];
  const key = `${formatDateISO(start)}:${aantalWeken}`;
  const taken = persons.length ? leesCache(sleutels.takenRange(takenArgs(persons, start, aantalWeken))) : { waarde: {} };
  const emails = agendaEmails(persons);
  const agenda = emails.length ? leesAgendaCache(agendaArgs(persons, start, aantalWeken)) : { waarde: LEGE_AGENDA };
  return {
    persons,
    basis: taken ? { key, persons, tasksByPersonId: taken.waarde || {} } : null,
    agenda: agenda ? { key, cal: knipAgenda(agenda.waarde, periode(start, aantalWeken).endDate), fout: false } : null,
  };
}

export default function CapacityOverview() {
  useKlantAfkortingen(); // klant-afkortingen laden vóór de labels renderen
  // Breedte van de taaknaam-kolom. Blijft staan tussen sessies, want wie hem één
  // keer breed zet wil hem de volgende dag niet opnieuw slepen. Alle opslag via
  // leesOpslag/schrijfOpslag: localStorage kan gooien (privémodus) of rommel bevatten.
  const [naamBreedte, setNaamBreedte] = useState(() => leesOpslag(NAAMKOLOM_KEY, KOLOM_STANDAARD, (ruw) => {
    const n = Number(ruw);
    return Number.isFinite(n) && n >= KOLOM_MIN && n <= KOLOM_MAX ? n : undefined;
  }));
  // Tijdens het slepen alleen de state; bij loslaten ook onthouden.
  const zetNaamBreedte = (px, opslaan = true) => {
    const v = begrensKolom(px);
    setNaamBreedte(v);
    if (opslaan) schrijfOpslag(NAAMKOLOM_KEY, v);
  };

  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  // 1/2/4 weken naast elkaar (Raijmond, wiki 24 aug + Teams 4 sep: "zolang ik maar
  // verder kan kijken dan 1 week"). Zelfde schakelaar als in de Planner To-Do.
  const [weken, setWeken] = useState(() => leesOpslag('capaciteit:weken', 1, (ruw) => ([1, 2, 4].includes(Number(ruw)) ? Number(ruw) : undefined)));
  const zetWeken = (n) => { setWeken(n); schrijfOpslag('capaciteit:weken', n); };
  // 'ruimte' (standaard: meeste vrije uren bovenaan — wie kan er nog werk bij?) of 'naam'.
  const [sortering, setSortering] = useState(() => leesOpslag('capaciteit:sortering', 'ruimte', (ruw) => (ruw === 'naam' ? 'naam' : 'ruimte')));
  // Weergave: standaard één rij per persoon met een bezettingsbalk; 'Per dag' toont het
  // volledige dagraster (onthouden per browser).
  const [perDag, setPerDag] = useState(() => leesOpslag('capaciteit:weergave', false, (ruw) => ruw === 'dag'));
  const zetPerDag = (v) => { setPerDag(v); schrijfOpslag('capaciteit:weergave', v ? 'dag' : 'overzicht'); };
  const zetSortering = (v) => { setSortering(v); schrijfOpslag('capaciteit:sortering', v); };
  // Aangeklikte cel: { id, iso, anchor } → popover met de taken van die dag + inplannen.
  const [celKeuze, setCelKeuze] = useState(null);
  const { rules, reload: herlaadRegels } = useCapacityRules();  // capaciteitsregels (planner)
  const [regelsOpen, setRegelsOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [functieFilter, setFunctieFilter] = useState('Alles');
  // Meerdere personen uit verschillende disciplines onder elkaar zetten om een
  // gezamenlijk moment te vinden (feedback #51). Leeg = de functiefilter geldt.
  const [gekozenPersonen, setGekozenPersonen] = useState([]);
  const [showStagiairs, setShowStagiairs] = useState(false);
  // Capaciteit gaat over het vaste team; freelancers alleen op verzoek (tool-feedback #33).
  const [showFreelancers, setShowFreelancers] = useState(false);

  const days = useMemo(() => {
    const result = [];
    for (let i = 0; i < weken * 7; i++) {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + i);
      const dayOfWeek = i % 7;
      if (dayOfWeek >= 5) continue;
      result.push({
        date, iso: formatDateISO(date), dayOfWeek,
        dayName: DAY_NAMES[dayOfWeek], display: formatDateDisplay(date),
        dayNumber: date.getDate(), isWeekend: false,
        weekStart: dayOfWeek === 0, weekNr: isoWeekNr(date),
      });
    }
    return result;
  }, [weekStart, weken]);

  // Eén rasterdefinitie voor hoofdtabel ÉN uitklap: de dagvakken liggen dan
  // altijd exact onder elkaar, en de breedteknop verstelt beide tegelijk
  // (feedback 20 aug: verschillende kolombreedtes bij openklappen verwarden).
  const dagMin = weken === 1 ? 100 : weken === 2 ? 88 : 74;
  const hoofdGrid = `${naamBreedte}px repeat(${days.length}, minmax(${dagMin}px, 1fr)) 70px`;
  // Zelfde dagkolommen, uitgeschreven: de blokken in de uitklap (Per dag) liggen erop.
  const dagSjabloon = Array(days.length).fill(`minmax(${dagMin}px, 1fr)`).join(' ');

  // ── Laden in twee sporen (Joris, 25 sep: "capaciteit laden duurt erg lang") ──
  // Taken + personen zijn er in ~1,5 s; de agenda-uren kosten veel langer. De lijst
  // staat dus zodra de taken binnen zijn en de agenda vult daarna in. Beide sporen
  // hebben een eigen buffer per periode, zodat terugbladeren direct is.
  const periodeKey = `${formatDateISO(weekStart)}:${weken}`;
  const keyRef = useRef(periodeKey);
  keyRef.current = periodeKey;
  // Beginstand uit de gedeelde planningcache: terug naar deze tab = meteen de vorige stand
  // (geen skelet), daarna stil verversen (Joris, 25 sep).
  const beginRef = useRef();
  if (beginRef.current === undefined) beginRef.current = capaciteitUitCache(weekStart, weken);
  const [basis, setBasis] = useState(() => beginRef.current?.basis || null);     // { key, persons, tasksByPersonId }
  const [agenda, setAgenda] = useState(() => beginRef.current?.agenda || null);   // { key, cal, fout }
  const [agendaBezig, setAgendaBezig] = useState(false);
  // Personen komen vóór de taken binnen: dan staan de namen al, met stille
  // placeholders in plaats van cijfers. "0% · 40u vrij" zolang de taken nog laden
  // las als echte data ("niemand heeft werk") en sprong daarna om.
  const [personenVast, setPersonenVast] = useState(() => beginRef.current?.persons || null);
  const { bezig: verversend, volg: volgVerversen } = useTraagVerversen();
  // Draaidagen uit de draaidagenbron: volle dag bezet (Optie gestreept), ook als er geen
  // taak met Type=Draaidag bestaat (Joris, 25 sep: Pepijn 28 sep Keukenkampioen stond op 72%).
  const [draaidagen, setDraaidagen] = useState(() => ({ key: periodeKey, lijst: draaidagenUitCache(formatDateISO(weekStart), periode(weekStart, weken).endDate) || [] }));

  // Laden via de gedeelde planningcache (utils/planningBronnen): personen en agenda delen
  // hun sleutel met het Rooster, taken per periode. De cache bewaart geen mislukte of
  // onvolledige agenda (niet-gelezen mailboxen) en laat een lading die vóór een verversing
  // vertrok niet meer landen. Hier nog een vraagteller: alleen de laatste fetchData mag de
  // state zetten, ook als een oudere ronde voor dezelfde periode later terugkomt.
  const vraagRef = useRef(0);
  // Verzetten vanuit de uitklap (zie verzetTaken). Elke verzetting (en elk terugzetten)
  // verhoogt de ronde: een lading die vóór die wijziging vertrok, mag de stand daarna niet
  // meer vervangen (de verzetting ververst zelf). Lopende verzettingen (taak-id →
  // { token, startDate, endDate }) liggen over elke lading heen tot hun PATCH klaar is.
  const verzetRondeRef = useRef(0);
  const onderwegRef = useRef(new Map());
  /** Taken van een lading tonen — tenzij er intussen verzet is; lopende verzettingen erover. */
  const zetTakenLading = useCallback((ronde, waarde) => {
    if (verzetRondeRef.current !== ronde) return;
    setBasis({ ...waarde, tasksByPersonId: zetDatums(waarde.tasksByPersonId || {}, [...onderwegRef.current.values()]) });
  }, []);
  // Stopt het doorvragen naar een verse agenda van een vorige ronde (ander venster, weg).
  const agendaStopRef = useRef(null);
  useEffect(() => () => agendaStopRef.current?.abort(), []);
  const fetchData = useCallback(({ ververs = false } = {}) => {
    const key = `${formatDateISO(weekStart)}:${weken}`;
    const vraag = ++vraagRef.current;
    const ronde = verzetRondeRef.current;
    agendaStopRef.current?.abort();
    const agendaStop = new AbortController();
    agendaStopRef.current = agendaStop;
    const nogActueel = () => vraagRef.current === vraag && keyRef.current === key;
    const bij = (fn) => (nieuw) => { if (nogActueel()) fn(nieuw); };
    const forceer = ververs;
    setError(null);
    const buren = [-1, 1].map(r => addDays(getMonday(weekStart), r * 7));
    const tot = periode(weekStart, weken).endDate;

    // Wat er al ligt meteen tonen (terugkeren, terugbladeren): geen skelet.
    const bewaard = !forceer ? capaciteitUitCache(weekStart, weken) : null;
    if (bewaard?.persons) setPersonenVast(bewaard.persons);
    if (bewaard?.basis) zetTakenLading(ronde, bewaard.basis);
    if (bewaard?.agenda) { setAgenda(bewaard.agenda); setAgendaBezig(false); } else setAgendaBezig(true);

    const personenBelofte = haalPersonen({ forceer, bijVerversing: bij(p => setPersonenVast(p || [])) });
    personenBelofte.then(p => { if (nogActueel()) setPersonenVast(p); }).catch(() => { /* de takenlading meldt de fout */ });

    // Spoor 1: personen + taken.
    const takenBelofte = personenBelofte.then(async (persons) => {
      if (!persons.length) return { persons: [], tasksByPersonId: {} };
      const args = takenArgs(persons, weekStart, weken);
      const tasksByPersonId = await haalTakenRange(args, {
        forceer,
        bijVerversing: bij(t => zetTakenLading(ronde, { key, persons, tasksByPersonId: t || {} })),
      });
      volgVerversen(lopendeLading(sleutels.takenRange(args)));
      return { persons, tasksByPersonId: tasksByPersonId || {} };
    });
    takenBelofte
      .then(res => { if (nogActueel()) zetTakenLading(ronde, { key, ...res }); })
      .catch(err => { if (nogActueel()) setError(`Kon data niet laden: ${err.message}`); })
      .finally(() => {
        // Buurweken alvast in de cache (zonder te wachten; fouten zijn hier niet interessant).
        personenBelofte.then(persons => buren.forEach(b => {
          if (persons.length) haalTakenRange(takenArgs(persons, b, weken)).catch(() => {});
        })).catch(() => {});
      });

    // Spoor 3: draaidagen (klein en snel; fouten maken de lijst niet stuk).
    const bewaardeDraaidagen = !forceer ? draaidagenUitCache(formatDateISO(weekStart), tot) : null;
    if (bewaardeDraaidagen) setDraaidagen({ key, lijst: bewaardeDraaidagen });
    haalDraaidagen(formatDateISO(weekStart), { tot, forceer })
      .then(lijst => { if (nogActueel()) setDraaidagen({ key, lijst }); })
      .catch(err => { console.warn('[capaciteit] draaidagen niet geladen:', err.message); });

    // Spoor 2: agenda-uren. Een gedeelde postbus (freelance@…) is geen eigen agenda.
    const zetAgenda = (ruw) => setAgenda({ key, cal: knipAgenda(ruw, tot), fout: false });
    personenBelofte.then(async (persons) => {
      const args = agendaArgs(persons, weekStart, weken);
      if (!args.emails.length) return LEGE_AGENDA;
      const res = await haalAgenda(args, { forceer, bijVerversing: bij(zetAgenda), signaal: agendaStop.signal });
      volgVerversen(lopendeLading(sleutels.agenda(args)));
      return res;
    })
      .then(ruw => { if (nogActueel()) zetAgenda(ruw); })
      // FE-06: niet stil 0 uur tonen — de waarschuwing zegt dat meetings nu ontbreken.
      // (De cache bewaart deze lege stand niet: het volgende bezoek probeert het opnieuw.)
      .catch(() => { if (nogActueel()) setAgenda({ key, cal: LEGE_AGENDA, fout: true }); })
      .finally(() => {
        if (nogActueel()) setAgendaBezig(false);
        // Buurweken pas ná de eigen agenda: die is het traagst en gaat voor.
        personenBelofte.then(persons => buren.forEach(b => {
          const args = agendaArgs(persons, b, weken);
          if (args.emails.length) haalAgenda(args).catch(() => {});
        })).catch(() => {});
      });
  }, [weekStart, weken, volgVerversen, zetTakenLading]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Verzetten vanuit de uitklap (blokken, Per dag) ──
  // Optimistisch: de datums meteen aanpassen, dan rekent de capaciteit (rij, %, Team
  // gemiddeld) direct opnieuw; daarna stil de taken van deze periode vers ophalen.
  // Bij een fout gaan de datums terug en komt er een melding (schrijfVerzetting).
  // Alles leest de actuele periode via refs: een undo of verversing die na het bladeren
  // afloopt, hoort bij het venster dat nú in beeld staat.
  useEffect(() => registreerOngedaanSneltoets(), []);
  const [openTaak, setOpenTaak] = useState(null);
  const basisRef = useRef(basis);
  basisRef.current = basis;
  const venster = useRef({ weekStart, weken });
  venster.current = { weekStart, weken };
  const eigenSleutel = () => {
    const b = basisRef.current;
    const v = venster.current;
    return b?.persons?.length ? sleutels.takenRange(takenArgs(b.persons, v.weekStart, v.weken)) : null;
  };
  const pasDatumsToe = (wijz) => {
    const sleutel = eigenSleutel();
    setBasis(prev => (prev ? { ...prev, tasksByPersonId: zetDatums(prev.tasksByPersonId, wijz) } : prev));
    if (sleutel) werkCacheBij(sleutel, (w) => zetDatums(w, wijz));
  };
  const zetOnderweg = (wijz, token) => wijz.forEach(w => onderwegRef.current.set(w.id, { ...w, token }));
  const haalOnderwegWeg = (token) => {
    [...onderwegRef.current.entries()].forEach(([id, w]) => { if (w.token === token) onderwegRef.current.delete(id); });
  };
  const verversTaken = async () => {
    const ronde = verzetRondeRef.current;
    const { weekStart: ws, weken: wk } = venster.current;
    const key = `${formatDateISO(ws)}:${wk}`;
    const persons = basisRef.current?.persons;
    if (!persons?.length) return;
    try {
      const t = await haalTakenRange(takenArgs(persons, ws, wk), { forceer: true });
      // Intussen opnieuw verzet of weggebladerd: dit antwoord is ouder dan wat er staat.
      if (keyRef.current !== key) return;
      zetTakenLading(ronde, { key, persons, tasksByPersonId: t || {} });
    } catch { /* stil: de stand blijft; Verversen of de volgende ronde haalt het op */ }
  };
  const verversRef = useRef(verversTaken);
  verversRef.current = verversTaken;
  const verzetTaken = async (plan, melding) => {
    const items = (plan || []).map(({ taak, startDate, endDate }) => {
      const s = String(taak.startDate || '').slice(0, 10) || null;
      const e = String(taak.endDate || taak.startDate || '').slice(0, 10) || null;
      return {
        taak,
        velden: { StartDateUitvoerder: startDate, EndDateUitvoerder: endDate },
        vorige: { StartDateUitvoerder: s, EndDateUitvoerder: e },
      };
    });
    if (!items.length) return;
    // Eén schrijfactie per taak tegelijk: anders kan een trage eerste PATCH ná de tweede
    // landen (taak op de verkeerde dag) of zet een mislukte eerste de tweede terug.
    if (items.some(p => onderwegRef.current.has(p.taak.id))) {
      toast('Deze taak wordt nog opgeslagen — probeer het zo nog eens', { icon: '⏳' });
      return;
    }
    verzetRondeRef.current += 1;
    const token = {};
    let terugToken = null;
    const naar = (lijst) => lijst.map(p => ({ id: p.taak.id, startDate: p.velden.StartDateUitvoerder, endDate: p.velden.EndDateUitvoerder }));
    const terug = (lijst) => lijst.map(p => ({ id: p.taak.id, startDate: p.vorige.StartDateUitvoerder, endDate: p.vorige.EndDateUitvoerder }));
    const ongeldig = () => taakGewijzigd({ behalve: [eigenSleutel()] });
    ongeldig();
    zetOnderweg(naar(items), token);
    pasDatumsToe(naar(items));
    const { mislukt } = await schrijfVerzetting(items, {
      melding,
      foutMelding: items.length === 1 ? 'Kon taak niet verplaatsen' : undefined,
      naGelukt: ongeldig,
      bijMislukt: (m) => {
        verzetRondeRef.current += 1;
        const ids = new Set(m.map(p => p.taak.id));
        [...onderwegRef.current.entries()].forEach(([id, w]) => { if (w.token === token && ids.has(id)) onderwegRef.current.delete(id); });
        pasDatumsToe(terug(m));
      },
      bijTerug: (g) => {
        // Ongedaan maken terwijl een nieuwere verzetting van dezelfde taak nog opslaat: niet
        // doen (die zou dan overschreven worden); de melding zegt het.
        if (g.some(p => onderwegRef.current.has(p.taak.id))) throw new Error('de taak wordt nog opgeslagen — sleep hem straks zelf terug');
        verzetRondeRef.current += 1;
        ongeldig();
        terugToken = {};
        zetOnderweg(terug(g), terugToken);
        pasDatumsToe(terug(g));
      },
      // Na een mislukte actie of na terugzetten: de terug-PATCHes zijn klaar, dus die
      // datums hoeven niet meer over de verversing heen.
      herlaad: async () => { if (terugToken) haalOnderwegWeg(terugToken); await verversRef.current(); },
    });
    haalOnderwegWeg(token);
    if (!mislukt.length) await verversRef.current();
  };

  // Taken van déze periode; zolang die er niet zijn: skeletrijen.
  const basisHier = basis?.key === periodeKey ? basis : null;
  const agendaHier = agenda?.key === periodeKey ? agenda : null;
  const agendaFout = agendaHier?.fout === true;
  // Zolang de taken van deze periode er niet zijn: alleen de namen (zonder taken en
  // agenda) — de rijen tonen dan placeholders, nooit "0%".
  const wachtOpTaken = !basisHier;
  const draaidagenHier = draaidagen?.key === periodeKey ? draaidagen.lijst : null;
  const data = useMemo(() => {
    if (basisHier) {
      const { startDate, endDate } = periode(weekStart, weken);
      const extra = draaidagTakenPerPersoon(draaidagenHier || [], basisHier.persons, basisHier.tasksByPersonId, { van: startDate, tot: endDate });
      const taken = { ...basisHier.tasksByPersonId };
      Object.entries(extra).forEach(([id, lijst]) => { taken[id] = [...(taken[id] || []), ...lijst]; });
      // De Outlook-afspraak van de shoot zelf is geen extra werk bovenop de draaidag.
      const alle = alleDraaidagTaken(draaidagenHier || [], basisHier.persons, basisHier.tasksByPersonId, { van: startDate, tot: endDate });
      const cal = agendaZonderDraaidag(agendaHier?.cal || LEGE_AGENDA, basisHier.persons, alle);
      return bouwWeekData(basisHier.persons, taken, cal);
    }
    if (personenVast?.length) return bouwWeekData(personenVast, {}, LEGE_AGENDA);
    return { persons: [], assignees: [], tasksByAssignee: {}, calByName: {}, agendaByName: {}, afwezigByName: {}, eventsByName: {}, agendaOnbekendByName: {}, labels: {} };
  }, [basisHier, agendaHier, personenVast, draaidagenHier, weekStart, weken]);
  const personen = data.persons;
  const calHoursByName = data.calByName;
  // Personen zijn week-onafhankelijk: de keuzelijst blijft staan terwijl een nieuwe
  // periode laadt (anders klapt de personenkeuze leeg bij doorbladeren).
  const vastePersonen = basis?.persons || personenVast;
  const vasteLabels = useMemo(() => (vastePersonen ? persoonLabels(vastePersonen) : {}), [vastePersonen]);
  // De keuzelijst volgt dezelfde vinkjes als de rijen: geen freelancers (en stagiairs)
  // tenzij aangevinkt (Joris, 26 sep: "hier hoeven geen freelancers te staan").
  // Wie al gekozen is blijft staan, anders verdwijnt een chip bij het uitvinken.
  const persoonOpties = useMemo(() => {
    const typeVan = new Map((vastePersonen || []).map(p => [p.id, safeStr(p.type)]));
    const toegestaan = Object.keys(vasteLabels).filter(id => magInKeuzelijst(typeVan.get(id), { showFreelancers, showStagiairs })
      || gekozenPersonen.includes(id));
    return sorteerPersonen(toegestaan, 'naam', vasteLabels);
  }, [vasteLabels, vastePersonen, showFreelancers, showStagiairs, gekozenPersonen]);
  // Weergavenaam per persoon-id (uniek, ook bij gelijke voornamen).
  const naamVan = (id) => data.labels?.[id] || vasteLabels[id] || id;


  // Map persoon-id → functie
  const personFuncties = useMemo(() => {
    const map = {};
    personen.forEach(p => {
      map[p.id] = safeStr(p.function);
    });
    return map;
  }, [personen]);

  // Map person name → type (Medewerker / Freelancer / Stagiair). Airtable kan het veld
  // als array teruggeven (multipleSelects) — naar tekst, anders matcht geen enkel filter.
  const personTypes = useMemo(() => {
    const map = {};
    personen.forEach(p => {
      map[p.id] = Array.isArray(p.type) ? p.type.join(', ') : String(p.type || '');
    });
    return map;
  }, [personen]);

  // Only show these functies as filter chips
  const allFuncties = ['Animator', 'Cameraman', 'Editor', 'Regisseur', 'Social Media Manager'];

  // Filtered assignees: apply functie filter + stagiair toggle
  const filteredAssignees = useMemo(() => {
    const filterLower = safeLower(functieFilter);
    if (gekozenPersonen.length) {
      // Expliciete keuze wint van functie- en stagiairfilter, in de gekozen volgorde.
      return gekozenPersonen.filter((name) => data.assignees.includes(name));
    }
    return data.assignees.filter(name => {
      // Stagiair- en freelancerfilter
      if (!magInKeuzelijst(personTypes[name], { showFreelancers, showStagiairs })) return false;
      // Functie filter
      if (functieFilter === 'Alles') return true;
      return safeLower(personFuncties[name]).includes(filterLower);
    });
  }, [data.assignees, functieFilter, personFuncties, personTypes, showStagiairs, showFreelancers, gekozenPersonen]);

  // Capacity per assignee — gedeelde util (identiek aan Planner To-Do)
  const dayISOs = useMemo(() => days.map(d => d.iso), [days]);
  // Uren én dagstaat (vrij / draaidag) in één keer — die twee hoorden altijd al bij
  // elkaar, maar de indicator-stipjes werden hier apart en nét iets anders berekend.
  // Vakantie als getimede agenda-afspraak ("Vakantie" 08:30–17:00) = afwezig, geen
  // meeting-uren. Zelfde regels als de Planner To-Do (rooster.js).
  const verwerkt = useMemo(() => {
    const result = {};
    data.assignees.forEach(name => {
      result[name] = pasAfwezigheidToe(data.tasksByAssignee[name] || [], calHoursByName[name] || {},
        data.afwezigByName?.[name], data.eventsByName?.[name], rules.hoursPerDay);
    });
    return result;
  }, [data, calHoursByName, rules.hoursPerDay]);
  const takenVan = (name) => verwerkt[name]?.tasks || [];
  const dagBelasting = useMemo(() => {
    const result = {};
    data.assignees.forEach(name => {
      result[name] = computeDayLoad(verwerkt[name]?.tasks || [], dayISOs, verwerkt[name]?.cal || {}, rules);
    });
    return result;
  }, [data.assignees, verwerkt, dayISOs, rules]);
  const capacityData = useMemo(() => {
    const result = {};
    Object.entries(dagBelasting).forEach(([name, v]) => { result[name] = v.uren; });
    return result;
  }, [dagBelasting]);

  // Bezetting per persoon over de getoonde dagen: capaciteit (zonder afwezige dagen),
  // ingepland, vrij (vrij/draaidag = geen ruimte) en overboekt.
  const bezetting = useMemo(() => {
    const result = {};
    Object.entries(dagBelasting).forEach(([id, v]) => {
      result[id] = persoonBezetting(v.uren, dayISOs, rules.hoursPerDay, v.staat, v.extra);
    });
    return result;
  }, [dagBelasting, dayISOs, rules.hoursPerDay]);
  const vrijPerPersoon = useMemo(() => {
    const result = {};
    Object.entries(bezetting).forEach(([id, b]) => { result[id] = b.vrij; });
    return result;
  }, [bezetting]);
  // Overboekte dagen per persoon — los van het periodegemiddelde: 10u op maandag en
  // de rest leeg is gemiddeld 25%, maar maandag is gewoon te vol (Codex, 25 sep).
  const overDagenVan = useMemo(() => {
    const result = {};
    Object.entries(dagBelasting).forEach(([id, v]) => {
      result[id] = overboekteDagen(v.uren, dayISOs, rules.hoursPerDay, v.staat || {}, v.extra || {});
    });
    return result;
  }, [dagBelasting, dayISOs, rules.hoursPerDay]);
  // Rijvolgorde: een expliciete personenkeuze houdt haar eigen volgorde, tenzij je
  // op ruimte sorteert; anders alfabetisch op weergavenaam.
  // Wie overboekt is (>100%, of op één dag te vol) staat altijd bovenaan, anders valt
  // die bij 'Ruimte' buiten beeld.
  // Pas sorteren als taken én agenda binnen zijn (of de agenda definitief mislukte); tot dan
  // alfabetisch, daarna vast tot je zelf van sortering, periode of personenkeuze wisselt
  // (wiki 27 sep: rijen sprongen bij "agenda laden…", ook met iemand uitgeklapt).
  const volgordeRef = useRef(null);
  // Klaar = de agenda van déze periode is er (ook een mislukte levert een resultaat met fout).
  const volgordeKlaar = !wachtOpTaken && Boolean(agendaHier);
  const zichtbaar = useMemo(() => {
    const pct = {};
    const dagOver = {};
    Object.entries(bezetting).forEach(([id, b]) => {
      pct[id] = b.pct;
      dagOver[id] = heeftDagOverboeking(b, overDagenVan[id]);
    });
    const opNaam = () => (gekozenPersonen.length ? filteredAssignees : sorteerPersonen(filteredAssignees, 'naam', data.labels));
    const uit = stabieleVolgorde({
      ids: filteredAssignees,
      // Elke eigen keuze (periode, sortering, personen, functie, freelancers/stagiairs) = opnieuw sorteren.
      sleutel: [periodeKey, sortering, gekozenPersonen.join(','), functieFilter, showFreelancers, showStagiairs].join('|'),
      klaar: volgordeKlaar,
      bereken: () => overboektenBovenaan(
        sortering === 'ruimte' ? sorteerPersonen(filteredAssignees, 'ruimte', data.labels, vrijPerPersoon) : opNaam(),
        pct, dagOver,
      ),
      terugval: opNaam,
      vorige: volgordeRef.current,
    });
    volgordeRef.current = uit.stand;
    return uit.volgorde;
  }, [filteredAssignees, sortering, data.labels, vrijPerPersoon, gekozenPersonen, bezetting, overDagenVan, volgordeKlaar, periodeKey,
    functieFilter, showFreelancers, showStagiairs]);

  // Teamtotaal voor de gekozen periode en filters — één stille regel boven de tabel.
  const team = useMemo(() => teamTotaal(filteredAssignees, bezetting), [filteredAssignees, bezetting]);

  // Team averages (only filtered)
  const teamAverages = useMemo(() => {
    const avgs = {};
    const count = filteredAssignees.length || 1;
    days.forEach(d => {
      let sum = 0;
      filteredAssignees.forEach(name => { sum += (capacityData[name]?.[d.iso] || 0); });
      avgs[d.iso] = Math.round((sum / count) * 10) / 10;
    });
    return avgs;
  }, [capacityData, filteredAssignees, days]);

  // Navigatie zit in <WeekSelector> (vorige/volgende + datumprikker).
  const todayISO = formatDateISO(new Date());

  const toggleExpand = (name) => setExpanded(prev => ({ ...prev, [name]: !prev[name] }));

  // Vrije dagen vallen buiten het weekgemiddelde: drie volle dagen en twee dagen vakantie
  // is 100%, niet 60%.
  const weekAvg = (name) => weekAveragePct(capacityData[name] || {}, dayISOs, rules.hoursPerDay, dagBelasting[name]?.staat || {});
  const teamWeekAvg = () => {
    const total = days.reduce((s, d) => s + (teamAverages[d.iso] || 0), 0);
    return Math.round((total / (days.length * rules.hoursPerDay)) * 100);
  };

  // ── Gedeelde bouwstenen voor overzicht en dagraster (gewone functies, geen
  //    componenten: een component binnen de pagina remount bij elke render) ──
  const medewerkerKop = (
    <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
      <Typography sx={{ fontSize: FS.klein, fontWeight: 600, color: '#64748b', mr: 'auto' }}>Medewerker</Typography>
      {/* Wie kan er nog werk bij? 'Ruimte' (standaard) zet de meeste vrije uren bovenaan. */}
      {[
        { v: 'ruimte', label: 'Ruimte', aria: 'Sorteer op vrije ruimte' },
        { v: 'naam', label: 'A–Z', aria: 'Sorteer op naam' },
      ].map(({ v, label, aria }) => (
        <ButtonBase key={v} onClick={() => zetSortering(v)} aria-label={aria} aria-pressed={sortering === v}
          sx={{ fontSize: FS.klein, fontWeight: sortering === v ? 700 : 500, color: sortering === v ? '#1e293b' : '#94a3b8', borderRadius: 0.5, px: 0.25 }}>
          {label}
        </ButtonBase>
      ))}
    </Box>
  );

  const naamCel = (name, isExpanded) => {
    const functie = personFuncties[name] || '';
    return (
      <Box sx={{ px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        {isExpanded
          ? <ExpandIcon className="chevron" sx={{ fontSize: 18, color: '#cbd5e1', flexShrink: 0 }} />
          : <CollapseIcon className="chevron" sx={{ fontSize: 18, color: '#cbd5e1', flexShrink: 0 }} />}
        <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
          <Typography noWrap title={personen.find(p => p.id === name)?.name || ''}
            sx={{ fontWeight: 600, fontSize: FS.groot, lineHeight: 1.4, flexShrink: 0 }}>{naamVan(name)}</Typography>
          {functie && (
            <Typography noWrap sx={{ fontSize: FS.klein, color: '#94a3b8', minWidth: 0 }}>{functie.split(',')[0]}</Typography>
          )}
          {data.agendaOnbekendByName?.[name] && (
            <Tooltip arrow title="Outlook-agenda kon niet gelezen worden — meetings tellen voor deze persoon niet mee, dus de bezetting kan te laag zijn.">
              <Typography noWrap data-testid="agenda-onbekend" sx={{ fontSize: FS.klein, color: '#94a3b8', fontStyle: 'italic', flexShrink: 0 }}>
                agenda onbekend
              </Typography>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  // Dagkop: bij 2/4 weken het weeknummer als groepskop boven de vijf dagen, zodat de
  // dagkop zelf kort blijft ("Ma 21 sep") en niet meer over twee regels breekt.
  const dagKop = (eerste, laatste, metWeekKop = true) => (
    <>
      {weken > 1 && metWeekKop && (
        <Box sx={{ display: 'grid', gridTemplateColumns: hoofdGrid }}>
          <Box />
          {days.filter(d => d.weekStart).map(d => (
            <Box key={d.iso} sx={{ gridColumn: 'span 5', borderLeft: '1px solid #e2e8f0', px: 1, pt: 0.75 }}>
              <Typography sx={{ fontSize: FS.klein, color: '#94a3b8' }}>Week {d.weekNr}</Typography>
            </Box>
          ))}
          <Box />
        </Box>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: hoofdGrid }}>
        {/* De naamkolom is breder/smaller te slepen aan zijn rechterrand (Joris, 26 sep:
            "gewoon kolom slepen?" — eerst waren het twee pijltjes naast het woord taaknaam). */}
        <Box sx={{ position: 'relative', minWidth: 0 }}>
          {eerste}
          <KolomRand breedte={naamBreedte} onZet={zetNaamBreedte} />
        </Box>
        {days.map(d => (
          <Box key={d.iso} sx={{ textAlign: 'center', py: 1, borderLeft: weken > 1 && d.weekStart ? '1px solid #e2e8f0' : undefined }}>
            <Typography noWrap sx={{ fontSize: FS.klein, fontWeight: d.iso === todayISO ? 700 : 600, color: d.iso === todayISO ? ACCENT : '#64748b' }}>
              {d.dayName} {d.display}
            </Typography>
          </Box>
        ))}
        {laatste}
      </Box>
    </>
  );

  // Eén dagcel van één persoon: percentage, of een toestand (Afwezig / Draaidag /
  // Optie) op een bijna-wit vlak. Details — ook externe afspraken — in de tooltip;
  // klik opent de dag met "Werk inplannen".
  // compact = in de uitklap: klein, en de vrije uren van die dag in plaats van een
  // percentage ("5u", "vol", of "+2u" als het te vol is) — uren zijn wat een planner
  // nodig heeft om werk in te passen.
  const dagCel = (name, d, compact = false) => {
    const hours = capacityData[name]?.[d.iso] || 0;
    const pct = Math.round((hours / rules.hoursPerDay) * 100);
    const dayTasks = getTasksForDay(takenVan(name), d.iso);
    const externeAfspraken = (data.agendaByName?.[name]?.[d.iso] || []).filter(ev => ev.soort === 'extern');
    const staat = dagBelasting[name]?.staat?.[d.iso];
    const stijl = DAGSTAAT_STIJL[staat];
    const extra = dagBelasting[name]?.extra?.[d.iso] || 0;
    const niveau = celNiveau(hours, rules.hoursPerDay, staat, extra);
    const gekozen = celKeuze?.id === name && celKeuze?.iso === d.iso;
    const kies = (e) => { e.stopPropagation(); setCelKeuze({ id: name, iso: d.iso, anchor: e.currentTarget }); };
    const vrijDag = Math.max(0, rules.hoursPerDay - hours);
    const compactTekst = niveau === 'over'
      ? `+${urenH(staat ? extra : hours - rules.hoursPerDay)}u`
      : staat ? staatTekst(staat) : (vrijDag <= 0.05 ? 'vol' : `${urenH(vrijDag)}u`);
    // Een draaidag blijft altijd "Draaidag" (of "Optie") heten, ook als er iets bovenop
    // staat: dat conflict komt er klein en rood achter (+2u) in plaats van een rood "100%"
    // dat de draaidag verstopte (Joris, 26 sep). Bij één week ook het projectnummer.
    const draaidagNr = staat && staat !== 'vrij' && weken === 1
      ? (dayTasks.find(t => t.type === 'Draaidag' && (t.draaidagProjectnummer || t.projectNumber)) || {})
      : null;
    const nr = draaidagNr ? (draaidagNr.draaidagProjectnummer || draaidagNr.projectNumber || '') : '';
    const staatInhoud = staat ? (
      <>
        {staatTekst(staat)}{nr ? <Box component="span" sx={{ color: '#475569' }}> · {nr}</Box> : null}
        {niveau === 'over' && <Box component="span" sx={{ color: ROOD, fontWeight: 700 }}> +{urenH(extra)}u</Box>}
      </>
    ) : null;
    return (
      <Tooltip key={d.iso} arrow placement="top" disableHoverListener={gekozen}
        title={stijl || dayTasks.length > 0 || externeAfspraken.length > 0 ? (
          <DagInhoud titel={`${naamVan(name)} - ${d.dayName} ${d.display} · ${urenH(stijl ? rules.hoursPerDay : hours)}u`} stijl={stijl} extra={extra}
            externeAfspraken={externeAfspraken} dayTasks={dayTasks} iso={d.iso} dayISOs={dayISOs} rules={rules}
            perTaak={dagBelasting[name]?.perTaak} />
        ) : ''}>
        <Box role="button" tabIndex={0}
          aria-label={`${naamVan(name)} ${d.dayName} ${d.display}: taken bekijken of werk inplannen`}
          onClick={kies}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); kies(e); } }}
          sx={{
            textAlign: 'center', py: compact ? 0.5 : 0.75, minHeight: compact ? 28 : undefined, cursor: 'pointer', outline: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderLeft: weken > 1 && d.weekStart ? '1px solid #eef0f3' : undefined,
            boxShadow: gekozen ? `inset 0 0 0 2px ${ACCENT}` : undefined,
            '&:hover, &:focus-visible': { boxShadow: 'inset 0 0 0 1px #cbd5e1' },
            // `background`: een optie-draaidag krijgt een gestreepte vulling (gradient).
            background: celVlak(staat, niveau),
          }}>
          <Typography noWrap sx={{ fontSize: compact || staat ? FS.klein : FS.groot, fontWeight: niveau === 'over' && (compact || !staat) ? 700 : 500, color: staat && !compact ? CEL_TEKST[staat] : (compact && niveau === 'leeg' ? '#64748b' : CEL_TEKST[niveau]), fontVariantNumeric: 'tabular-nums', ...voorlopigSx }}>
            {compact ? compactTekst : (staat ? staatInhoud : `${pct}%`)}
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  // Eerste keer (of een periode die nog niet in de buffer zit): het scherm staat meteen,
  // met stille skeletrijen op de plek van de lijst — geen spinner over alles heen.
  const skelet = (
    <Box aria-busy="true" aria-label="Capaciteit laden">
      {[0, 1, 2, 3, 4, 5].map(i => (
        <Box key={i} sx={{ display: 'grid', gridTemplateColumns: OVERZICHT_GRID, alignItems: 'center', borderBottom: '1px solid #f1f5f9', py: 1.1 }}>
          <Box sx={{ px: 2 }}><Skeleton variant="text" width={`${55 - i * 4}%`} sx={{ fontSize: FS.groot }} /></Box>
          <Box sx={{ px: 1.5 }}><Skeleton variant="rounded" height={6} /></Box>
          <Box sx={{ pr: 1, display: 'flex', justifyContent: 'flex-end' }}><Skeleton variant="text" width={28} /></Box>
          <Box sx={{ pr: 2, display: 'flex', justifyContent: 'flex-end' }}><Skeleton variant="text" width={56} /></Box>
        </Box>
      ))}
    </Box>
  );
  // Nog geen personen: het hele skelet. Wel personen, nog geen taken: rijen met namen
  // en per rij een zachte placeholder (plek) waar straks de cijfers staan.
  const laadt = wachtOpTaken && !data.assignees.length;
  const plek = (breedte, hoogte) => (
    <Skeleton variant={hoogte ? 'rounded' : 'text'} width={breedte} height={hoogte} data-testid="cijfer-laden" />
  );
  // Taken zijn er, de agenda nog niet: cijfers staan, maar gedempt — ze kunnen nog schuiven.
  const voorlopig = agendaBezig && !wachtOpTaken;
  const voorlopigSx = { opacity: voorlopig ? 0.5 : 1, transition: 'opacity 0.2s' };

  return (
    // Rechts extra ruimte op desktop: de zwevende feedback-/assistentknoppen
    // (DOCK_FAB_SX: right 24 + 46 breed) vielen anders over de Week-kolom.
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', pl: { xs: 2, sm: 3, md: 5 }, pr: { xs: 2, sm: 3, md: 10 }, pt: { xs: 1.5, md: 2 }, pb: 10 }}>
      {/* Taken die al op Afgerond staan en daardoor onzichtbaar zijn (9 okt) — alleen als er zijn */}
      <AfgerondSignaal />
      {/* Werkbalk: "Capaciteit" staat als tab in de kopbalk (PlanningTabs) */}
      <PageHeader
        kaal
        title="Bezetting per medewerker per dag"
        rightActions={(
          <>
            {verversend && (
              <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircularProgress size={10} thickness={6} sx={{ color: '#94a3b8' }} />
                <Typography component="span" sx={{ fontSize: FS.klein, color: '#94a3b8' }}>bijwerken…</Typography>
              </Box>
            )}
            <WeekSelector weekStart={weekStart} onChange={setWeekStart} />
            <ToggleButtonGroup size="small" exclusive value={weken} onChange={(_e, v) => v && zetWeken(v)}
              sx={{ bgcolor: '#fff', '& .MuiToggleButton-root': { textTransform: 'none', px: 1.1, py: 0.35, fontSize: 12, fontWeight: 700 } }}>
              <ToggleButton value={1}>1 week</ToggleButton>
              <ToggleButton value={2}>2 weken</ToggleButton>
              <ToggleButton value={4}>4 weken</ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Zo rekenen we dit uit">
              <IconButton size="small" onClick={() => setRegelsOpen(true)} aria-label="Zo rekenen we dit uit">
                <HelpIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Vernieuwen">
              <IconButton onClick={() => fetchData({ ververs: true })} size="small" aria-label="Vernieuwen"><RefreshIcon /></IconButton>
            </Tooltip>
          </>
        )}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={() => fetchData({ ververs: true })} sx={{ textTransform: 'none', fontWeight: 700 }}>Opnieuw</Button>}>
          {error}
        </Alert>
      )}

      {agendaFout && (
        <Alert severity="warning" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={() => fetchData({ ververs: true })} sx={{ textTransform: 'none', fontWeight: 700 }}>Opnieuw</Button>}>
          Agenda-uren niet geladen — de bezetting kan te laag lijken (meetings tellen nu niet mee).
        </Alert>
      )}

      {/* Filters — één rustige regel: functie, personen, en wie er meetelt. Geen pillen:
          een keuzelijst en twee vinkjes zeggen hetzelfde met minder inkt. */}
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <TextField select size="small" label="Functie" value={functieFilter}
          onChange={(e) => setFunctieFilter(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="Alles">Alle functies</MenuItem>
          {allFuncties.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
        </TextField>
        <Autocomplete
          multiple size="small" options={persoonOpties} value={gekozenPersonen}
          onChange={(_, v) => setGekozenPersonen(v)} disableCloseOnSelect limitTags={3}
          getOptionLabel={(id) => naamVan(id)}
          sx={{ minWidth: 300 }}
          slotProps={{ paper: { sx: { minWidth: 340 } } }}
          renderInput={(params) => <TextField {...params} label="Personen naast elkaar" placeholder={gekozenPersonen.length ? '' : 'Kies meerdere…'} />}
          // Naam op één regel, functies als één grijze regel eronder (afgekapt) — de oude
          // variant liet de functies om de naam heen wikkelen (Joris, 15 sep).
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option} style={{ ...props.style, alignItems: 'center', paddingTop: 4, paddingBottom: 4 }}>
              <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.25 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.2 }}>{naamVan(option)}</Typography>
                {personFuncties[option] && (
                  <Typography noWrap sx={{ fontSize: '0.7rem', color: '#94a3b8', lineHeight: 1.2 }} title={personFuncties[option]}>
                    {personFuncties[option]}
                  </Typography>
                )}
              </Box>
            </li>
          )}
        />
        <Box sx={{ flex: 1 }} />
        {[
          { label: 'Stagiairs', aan: showStagiairs, zet: setShowStagiairs },
          { label: 'Freelancers', aan: showFreelancers, zet: setShowFreelancers },
        ].map(({ label, aan, zet }) => (
          <FormControlLabel key={label} sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: FS.klein, color: '#64748b' } }}
            control={<Checkbox size="small" checked={aan} onChange={() => zet(!aan)} sx={{ p: 0.5, color: '#cbd5e1' }} />}
            label={label} />
        ))}
      </Box>

      {/* Mobiele weergave: compacte kaart per persoon (snel scannen) */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {laadt && skelet}
        {zichtbaar.map(name => {
          const cap = capacityData[name] || {};
          const avg = weekAvg(name);
          const isExp = expanded[name];
          const tasks = takenVan(name);
          const staat = dagBelasting[name]?.staat || {};
          const extra = dagBelasting[name]?.extra || {};
          const over = overDagenVan[name] || [];
          const vrij = vrijPerPersoon[name] || 0;
          if (wachtOpTaken) {
            return (
              <Stack key={name} aria-busy="true" direction="row" alignItems="center" spacing={0.75} sx={{ py: 1, mb: 0.5, pl: 3, borderBottom: '1px solid #f1f5f9' }}>
                <Typography sx={{ fontWeight: 600, fontSize: FS.groot, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{naamVan(name)}</Typography>
                {plek(28)}
                {plek(44)}
              </Stack>
            );
          }
          return (
            <Box key={name} sx={{ mb: 0.5, borderBottom: '1px solid #f1f5f9', ...voorlopigSx }}>
              <Box onClick={() => toggleExpand(name)} sx={{ py: 1, cursor: 'pointer' }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  {isExp ? <ExpandIcon sx={{ fontSize: 18, color: '#cbd5e1' }} /> : <CollapseIcon sx={{ fontSize: 18, color: '#cbd5e1' }} />}
                  <Typography sx={{ fontWeight: 600, fontSize: FS.groot, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{naamVan(name)}</Typography>
                  {over.length > 0 && <Box aria-label={`Overboekt op ${over.map(fmtDagKort).join(', ')}`} sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: ROOD }} />}
                  <Typography sx={{ fontSize: FS.klein, fontWeight: isOverSignaal(avg) ? 700 : 500, color: isOverSignaal(avg) ? ROOD : '#94a3b8' }}>{avg}%</Typography>
                  <Typography sx={{ fontSize: FS.groot, fontWeight: 500, color: VRIJ_TOON, minWidth: 44, textAlign: 'right' }}>{fmtUrenHeel(vrij)}u</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, pl: 3 }}>
                  {days.map(d => {
                    const pct = Math.round(((cap[d.iso] || 0) / rules.hoursPerDay) * 100);
                    const niveau = celNiveau(cap[d.iso], rules.hoursPerDay, staat[d.iso], extra[d.iso]);
                    return (
                      <Box key={d.iso} sx={{ flex: 1, textAlign: 'center', py: 0.25, background: celVlak(staat[d.iso], niveau) }}>
                        <Typography sx={{ fontSize: FS.klein, color: '#475569', lineHeight: 1.2 }}>{(d.dayName || '').slice(0, 2)}</Typography>
                        <Typography sx={{ fontSize: FS.klein, fontWeight: niveau === 'over' ? 700 : 500, color: CEL_TEKST[niveau] }}>
                          {staat[d.iso] ? staatTekst(staat[d.iso]) : pct}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
              <Collapse in={isExp}>
                <Box sx={{ borderTop: '1px solid #f1f5f9', bgcolor: '#fafbfc' }}>
                  <ExpandedTasks tasks={tasks} days={days} rules={rules} variant="list" />
                </Box>
              </Collapse>
            </Box>
          );
        })}
        {!laadt && filteredAssignees.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>Geen medewerkers.</Typography>
        )}
      </Box>

      {/* Teamtotaal voor de gekozen periode en filters — één stille regel — en de
          weergavekeuze: overzicht (één balk per persoon) of het volledige dagraster. */}
      <Stack direction="row" alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', columnGap: 2, rowGap: 0.5 }}>
        <Typography data-testid="teamtotaal" sx={{ fontSize: FS.klein, color: '#64748b', flex: 1, minWidth: 0, fontVariantNumeric: 'tabular-nums' }}>
          {wachtOpTaken ? 'Team: laden…' : (
            <>
              <Box component="span" sx={voorlopigSx}>
                Team: {fmtUrenHeel(team.capaciteit)}u capaciteit · {fmtUrenHeel(team.ingepland)}u ingepland · {fmtUrenHeel(team.vrij)}u vrij
                {team.overboekt > 0.05 && (
                  <Box component="span" sx={{ color: ROOD }}> · {fmtUrenHeel(team.overboekt)}u overboekt</Box>
                )}
              </Box>
              {/* De lijst staat al; de agenda (meetings) vult nog in. */}
              {agendaBezig && (
                <Box component="span" data-testid="agenda-laden" sx={{ color: '#94a3b8' }}> · agenda laden…</Box>
              )}
            </>
          )}
        </Typography>
        <ToggleButtonGroup size="small" exclusive value={perDag ? 'dag' : 'overzicht'}
          onChange={(_e, v) => v && zetPerDag(v === 'dag')} aria-label="Weergave"
          sx={{ display: { xs: 'none', md: 'inline-flex' }, bgcolor: '#fff', '& .MuiToggleButton-root': { textTransform: 'none', px: 1.1, py: 0.25, fontSize: 12, fontWeight: 600 } }}>
          <ToggleButton value="overzicht">Overzicht</ToggleButton>
          <ToggleButton value="dag">Per dag</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Desktop/tablet (op mobiel verborgen; daar staat de lijst) */}
      <Paper elevation={0} sx={{ display: { xs: 'none', md: 'block' }, border: '1px solid #eef0f3', borderRadius: 2, overflow: 'hidden', overflowX: 'auto' }}>
        {laadt ? skelet : !perDag ? (
          <>
            {/* ── Overzicht: per persoon één rij met één bezettingsbalk ── */}
            <Box sx={{ display: 'grid', gridTemplateColumns: OVERZICHT_GRID, borderBottom: '1px solid #e2e8f0' }}>
              {medewerkerKop}
              <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ fontSize: FS.klein, fontWeight: 600, color: '#64748b' }}>
                  Bezetting {weken === 1 ? 'deze week' : `${weken} weken`}
                </Typography>
              </Box>
              <Box />
              <Box sx={{ pr: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <Typography sx={{ fontSize: FS.klein, fontWeight: 600, color: '#64748b' }}>Vrij</Typography>
              </Box>
            </Box>
            {zichtbaar.map(name => {
              const b = bezetting[name] || { pct: 0, vrij: 0, ingepland: 0, capaciteit: 0 };
              const isExpanded = expanded[name];
              const over = isOverSignaal(b.pct);
              const overDagen = overDagenVan[name] || [];
              // Gemiddeld nog ruimte, maar op een dag te vol: klein rood signaal bij het %.
              const dagOver = !over && heeftDagOverboeking(b, overDagen);
              const overTekst = overDagen.map(iso => `${fmtDagKort(iso)} (${urenH(capacityData[name]?.[iso])}u)`).join(', ');
              return (
                <Box key={name}>
                  {wachtOpTaken ? (
                    <Box aria-busy="true" sx={{ display: 'grid', gridTemplateColumns: OVERZICHT_GRID, alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
                      {naamCel(name, false)}
                      <Box sx={{ px: 1.5, py: 1.1 }}>{plek('100%', 6)}</Box>
                      <Box sx={{ pr: 1, display: 'flex', justifyContent: 'flex-end' }}>{plek(28)}</Box>
                      <Box sx={{ pr: 2, display: 'flex', justifyContent: 'flex-end' }}>{plek(56)}</Box>
                    </Box>
                  ) : (
                  <Box onClick={() => toggleExpand(name)} sx={{
                    display: 'grid', gridTemplateColumns: OVERZICHT_GRID, alignItems: 'center',
                    cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                    '&:hover': { backgroundColor: '#fafbfc' }, '&:hover .chevron': { color: '#64748b' },
                  }}>
                    {naamCel(name, isExpanded)}
                    <Box sx={{ px: 1.5, py: 1.1, display: 'flex', alignItems: 'center', ...voorlopigSx }}>
                      {b.pct === null ? (
                        <Typography sx={{ fontSize: FS.klein, color: '#94a3b8' }}>Afwezig</Typography>
                      ) : (
                        <Bezettingsbalk pct={b.pct}
                          label={`${naamVan(name)}: ${b.pct}% bezet${over ? ', overboekt' : dagOver ? ', overboekt op een dag' : ''}`} />
                      )}
                    </Box>
                    <Tooltip arrow placement="top"
                      title={`${fmtUrenHeel(b.ingepland)}u ingepland van ${fmtUrenHeel(b.capaciteit)}u${overDagen.length ? ` · overboekt op ${overTekst}` : ''}`}>
                      <Typography sx={{ fontSize: FS.groot, textAlign: 'right', pr: 1, fontVariantNumeric: 'tabular-nums',
                        fontWeight: over ? 700 : 500, color: over ? ROOD : '#475569', ...voorlopigSx }}>
                        {dagOver && (
                          <Box component="span" data-testid="dag-overboekt" aria-label={`Overboekt op ${overTekst}`}
                            sx={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', bgcolor: ROOD, mr: 0.75, verticalAlign: 'middle' }} />
                        )}
                        {b.pct === null ? '' : `${b.pct}%`}
                      </Typography>
                    </Tooltip>
                    <Typography sx={{ fontSize: FS.groot, textAlign: 'right', pr: 2, fontVariantNumeric: 'tabular-nums',
                      fontWeight: 500, color: VRIJ_TOON, ...voorlopigSx }}>
                      {fmtUrenHeel(b.vrij)}u vrij
                    </Typography>
                  </Box>
                  )}
                  {/* Uitklap: het dagraster van deze persoon + de taken per dag. */}
                  <Collapse in={isExpanded} unmountOnExit>
                    <Box sx={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#fafbfc', overflowX: 'auto' }}>
                      {/* Eén kopregel met de dagen, één rij vrije uren per dag, dan de regels per project. */}
                      {dagKop(<Box />, <Box />, false)}
                      <Box sx={{ display: 'grid', gridTemplateColumns: hoofdGrid, borderBottom: '1px solid #eef0f3' }}>
                        <Box sx={{ px: 2, display: 'flex', alignItems: 'center' }}>
                          <Typography sx={{ fontSize: FS.klein, color: '#94a3b8' }}>Vrij</Typography>
                        </Box>
                        {days.map(d => dagCel(name, d, true))}
                        <Box />
                      </Box>
                      <ExpandedTasks tasks={takenVan(name)} days={days} rules={rules} gridCols={hoofdGrid} />
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </>
        ) : (
          <>
            {/* ── Per dag: het volledige raster ── */}
            <Box sx={{ borderBottom: '1px solid #e2e8f0' }}>
              {dagKop(medewerkerKop, (
                <Box sx={{ textAlign: 'right', pr: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <Typography sx={{ fontSize: FS.klein, fontWeight: 600, color: '#64748b' }}>Vrij</Typography>
                </Box>
              ))}
            </Box>
            {zichtbaar.map(name => {
              const b = bezetting[name] || { vrij: 0, pct: 0 };
              const isExpanded = expanded[name];
              if (wachtOpTaken) {
                return (
                  <Box key={name} aria-busy="true" sx={{ display: 'grid', gridTemplateColumns: hoofdGrid, alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
                    {naamCel(name, false)}
                    {days.map(d => <Box key={d.iso} sx={{ display: 'flex', justifyContent: 'center' }}>{plek(32)}</Box>)}
                    <Box sx={{ pr: 2, display: 'flex', justifyContent: 'flex-end' }}>{plek(32)}</Box>
                  </Box>
                );
              }
              return (
                <Box key={name}>
                  <Box onClick={() => toggleExpand(name)} sx={{
                    display: 'grid', gridTemplateColumns: hoofdGrid,
                    cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                    '&:hover': { backgroundColor: '#fafbfc' }, '&:hover .chevron': { color: '#64748b' },
                    transition: 'background-color 0.15s',
                  }}>
                    {naamCel(name, isExpanded)}
                    {days.map(d => dagCel(name, d))}
                    <Tooltip arrow placement="top" title={`${fmtUrenHeel(b.vrij)}u vrij · ${b.pct ?? 0}% bezet`}>
                      <Box sx={{ textAlign: 'right', pr: 2, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Typography sx={{ fontSize: FS.groot, fontWeight: 500, color: VRIJ_TOON, fontVariantNumeric: 'tabular-nums', ...voorlopigSx }}>
                          {fmtUrenHeel(b.vrij)}u
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Box>
                  {/* Uitklap: dezelfde blokken als het Rooster, op de dagkolommen hierboven.
                      Het percentage per dag blijft in de rij erboven staan. */}
                  <Collapse in={isExpanded} unmountOnExit>
                    <Box sx={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#fafbfc' }}>
                      <CapaciteitBlokken
                        persoon={{ id: name, name: naamVan(name) }}
                        taken={takenVan(name)}
                        events={data.eventsByName?.[name]}
                        afwezig={verwerkt[name]?.afwezig}
                        agendaPerDag={data.agendaByName?.[name]}
                        load={dagBelasting[name]}
                        days={days}
                        naamKolom={`${naamBreedte}px`}
                        dagSjabloon={dagSjabloon}
                        rules={rules}
                        onVerzet={verzetTaken}
                        onTaakOpen={setOpenTaak}
                      />
                    </Box>
                  </Collapse>
                </Box>
              );
            })}

            {/* Team average row */}
            {!wachtOpTaken && (
            <Box sx={{ display: 'grid', gridTemplateColumns: hoofdGrid, ...voorlopigSx }}>
              <Box sx={{ px: 2, py: 1, fontSize: FS.klein, color: '#64748b' }}>
                Team gemiddeld
              </Box>
              {days.map(d => {
                const hours = teamAverages[d.iso] || 0;
                const pct = Math.round((hours / rules.hoursPerDay) * 100);
                return (
                  <Tooltip key={d.iso} arrow placement="top" title={`Gemiddeld ${urenH(hours)}u per persoon`}>
                    <Box sx={{ textAlign: 'center', py: 1 }}>
                      <Typography sx={{ fontSize: FS.klein, color: '#64748b' }}>{pct}%</Typography>
                    </Box>
                  </Tooltip>
                );
              })}
              <Box sx={{ textAlign: 'right', pr: 2, py: 1 }}>
                <Typography sx={{ fontSize: FS.klein, color: '#64748b' }}>{teamWeekAvg()}%</Typography>
              </Box>
            </Box>
            )}
          </>
        )}
        {!laadt && filteredAssignees.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>Geen medewerkers.</Typography>
        )}
      </Paper>

      {/* Klik op een cel: wat staat er die dag, en direct werk inplannen. */}
      {celKeuze && (() => {
        const { id, iso } = celKeuze;
        const dag = days.find(d => d.iso === iso);
        const staat = dagBelasting[id]?.staat?.[iso];
        const stijl = DAGSTAAT_STIJL[staat];
        const uren = capacityData[id]?.[iso] || 0;
        const vrijDag = staat ? 0 : Math.max(0, rules.hoursPerDay - uren);
        const pct = Math.round((uren / rules.hoursPerDay) * 100);
        const dayTasks = getTasksForDay(takenVan(id), iso);
        const ext = (data.agendaByName?.[id]?.[iso] || []).filter(ev => ev.soort === 'extern');
        const sluit = () => setCelKeuze(null);
        return (
          <Popover open anchorEl={celKeuze.anchor} onClose={sluit}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            slotProps={{ paper: { sx: { width: 340, maxWidth: 'calc(100vw - 32px)', borderRadius: 2, p: 1.5 } } }}>
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', flex: 1, minWidth: 0 }} noWrap>{naamVan(id)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {dag?.date?.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'short' })}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: FS.klein, fontWeight: 600, color: '#475569' }}>
                {stijl ? staatTekst(staat) : `${pct}% · ${urenH(uren)}u gepland`}
              </Typography>
              <Typography sx={{ fontSize: FS.klein, fontWeight: 700, color: vrijDag > 0 ? ACCENT : '#94a3b8' }}>
                {vrijDag > 0 ? `${urenH(vrijDag)}u vrij` : 'geen ruimte'}
              </Typography>
            </Stack>
            {stijl || dayTasks.length || ext.length ? (
              <Box sx={{ maxHeight: 220, overflowY: 'auto', mx: -0.5 }}>
                <DagInhoud licht stijl={stijl} extra={dagBelasting[id]?.extra?.[iso] || 0}
                  externeAfspraken={ext} dayTasks={dayTasks} iso={iso} dayISOs={dayISOs} rules={rules}
                  perTaak={dagBelasting[id]?.perTaak} />
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" display="block">Nog niets gepland op deze dag.</Typography>
            )}
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={() => { if (!expanded[id]) toggleExpand(id); sluit(); }}
                sx={{ textTransform: 'none', fontWeight: 600, color: '#526075' }}>
                Hele week tonen
              </Button>
              <Button size="small" variant="contained" disableElevation startIcon={<InplannenIcon />}
                disabled={staat === 'vrij'}
                onClick={() => { sluit(); navigate(inplanLink(id, iso)); }}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                Werk inplannen
              </Button>
            </Stack>
          </Popover>
        );
      })()}

      {/* Uitleg + (voor de planner) de instelbare rekenregels */}
      <Dialog open={regelsOpen} onClose={() => setRegelsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Capaciteitsregels</DialogTitle>
        <DialogContent dividers>
          <CapaciteitsRegels onSaved={herlaadRegels} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegelsOpen(false)} sx={{ textTransform: 'none' }}>Sluiten</Button>
        </DialogActions>
      </Dialog>

      {/* Legenda — één stille regel tekst */}
      <Typography sx={{ mt: 1.5, fontSize: FS.klein, color: '#94a3b8', display: { xs: 'none', md: 'block' } }}>
        {perDag
          ? 'Percentage = bezetting van die dag · rood = meer dan 100% · Afwezig, Draaidag en Optie (gestreept) = niet beschikbaar · ~ = ingeschat · details en externe afspraken bij aanwijzen.'
          : 'Balk = ingepland deel van de beschikbare uren · streep = 100% · rood = overboekt · • = op een dag overboekt (beide bovenaan) · afwezige dagen tellen niet mee · ~ = ingeschat · klik een rij voor de dagen.'}
      </Typography>
      {/* Mobiel heeft geen balk: daar staan per dag de percentages en rechts de vrije uren. */}
      <Typography sx={{ mt: 1.5, fontSize: FS.klein, color: '#94a3b8', display: { xs: 'block', md: 'none' } }}>
        Per dag = bezetting van die dag · rechts gemiddelde en vrije uren · rood = meer dan 100% · • = op een dag overboekt · overboekten staan bovenaan · tik een naam voor de taken.
      </Typography>

      {/* Klik op een blok in de uitklap (Per dag): de taak openen, zoals in het Rooster. */}
      <TaakPaneel
        taak={openTaak}
        open={Boolean(openTaak)}
        onClose={() => setOpenTaak(null)}
        personen={vastePersonen || []}
        onBijgewerkt={() => { verzetRondeRef.current += 1; verversRef.current(); }}
        onVerwijderd={() => { setOpenTaak(null); verzetRondeRef.current += 1; verversRef.current(); }}
        onNaarProject={(pid) => { setOpenTaak(null); navigate(`/projects/${pid}`); }}
      />
    </Box>
  );
}
